import { Unarchive, Archive, Edit, Save, Close } from '@mui/icons-material';
import {
  Divider,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  TextField,
  Paper,
  Chip,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  forwardRef,
  useEffect,
  useState,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import moment from 'moment';
import { AlertDialog, AlertDialogProps } from '../dialogs';
import { StageButtonMenu, StageButtonMenuProps } from './StageButtonMenu';
import { Theme, styled, useTheme } from '@mui/material/styles';
import {
  UseMutation,
  UseLazyQuery,
  UseQueryState,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { DateChip } from '../items';
import { getStatusFromDate } from '../../utils/item.utils';
import { JFormik } from '../forms';
import * as Yup from 'yup';

export interface StageProps {
  documentId?: string;
  data?: any;
  reloadParent?: Function;
  key?: string;
  status?: StageButtonMenuProps;
  statuses?: any[];
  selected?: boolean;
  highlighted?: boolean;
  numChildren?: number;
  childrenPastDue?: number;
  childrenAtRisk?: number;
  statusCount?: { [key: string]: number };
  // color?: 'ontrack' | 'complete' | 'warning' | 'pastdue' | 'default';
  showDetails?: boolean;
  disabled?: boolean;
  template?: boolean;
  patchMutation?: UseMutation<any>;
  subscription?: UseQueryState<any>;
  subscriptionParams?: object;
  lazyQuery?: UseLazyQuery<any>;
  onDateChange?: Function;
  onStatusChange?: Function;
  onStatusInfo?: Function;
  active?: boolean;
}

export const Stage = forwardRef((props: StageProps, ref) => {
  const {
    documentId,
    data,
    reloadParent = () => {},
    key,
    statuses,
    selected,
    highlighted = false,
    numChildren = 0,
    childrenPastDue = 0,
    childrenAtRisk = 0,
    statusCount,
    // color = 'default',
    showDetails = false,
    disabled = false,
    template = false,
    patchMutation,
    subscription,
    subscriptionParams,
    lazyQuery,
    onDateChange = () => {},
    onStatusChange = () => {},
    onStatusInfo,
    active = false,
  } = props;
  const [alertDetails, setAlertDetails] = useState<AlertDialogProps>();

  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [value, setValue] = useState<string>(
    data?.title || data?.stage_r?.name || ''
  );
  const [stageStatus, setStatus] = useState<string>(data?.status || '');
  const [hover, setHover] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [prevValue, setPrevValue] = useState<string>(data?.title || '');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [patch, patchResult] = (patchMutation && patchMutation()) || [
    () => {},
    {},
  ];
  const [reload, reloadResult] = (lazyQuery && lazyQuery()) || [() => {}];
  const { data: stageCache } =
    (subscription && subscriptionParams && subscription(subscriptionParams)) ||
    {};
  const triggerReload = async () => {
    //not needed
  };
  useEffect(() => {
    const item: any = patchResult?.data;
    if (item) {
      setValue((pv: string) => item?.title || pv);
      setStatus((pv: string) => item?.status || pv);
    }
  }, [patchResult]);

  const [stageValues, setStageValues] = useState<any>({
    start_date: data?.start_date,
    due_date: data?.due_date,
    complete: !!data?.outcome,
    status: data?.outcome_r?.name,
  });

  useEffect(() => {
    const stage: any = stageCache;
    if (stage?.id) {
      setStageValues((pv: any) => ({
        ...pv,
        start_date: stage?.start_date,
        due_date: stage?.due_date,
      }));
    }
  }, [stageCache]);

  const scrollRef = useRef<HTMLElement>(null);
  const scrollToRef = () => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useImperativeHandle(ref, () => ({
    delete: () => {
      setOpen(true);
    },
    triggerReload: () => {
      triggerReload();
    },
    scrollRef: scrollRef,
  }));

  const numDays = Math.abs(
    moment(stageValues?.due_date).diff(moment.now(), 'days')
  );

  const [color, bordercolor, backgroundColor] = useMemo(() => {
    const color = stageValues?.complete
      ? 'complete'
      : getStatusFromDate(stageValues?.due_date || stageValues?.start_date);
    switch (color) {
      case 'ontrack':
        return [
          color,
          theme.palette.secondary.main,
          `${theme.palette.secondary.main}1f`,
        ];
      case 'complete':
        return [
          color,
          theme.palette.primary.main,
          `${theme.palette.primary.main}1f`,
        ];
      case 'warning':
        return [
          color,
          theme.palette.warning.main,
          `${theme.palette.warning.main}1f`,
        ];
      case 'pastdue':
        return [
          color,
          theme.palette.error.main,
          `${theme.palette.error.main}1f`,
        ];
      default:
        if (active) {
          return ['default', theme.palette.text.secondary, `#ffffff1f`];
        } else {
          return ['default', theme.palette.divider, `#ffffff3f`];
        }
    }
  }, [stageValues?.complete, stageValues?.due_date, stageValues?.start_date]);
  return (
    <Paper
      id={documentId}
      ref={ref as any}
      elevation={highlighted ? 10 : 1}
      onMouseEnter={() => setHover(true && !disabled && !template)}
      onMouseLeave={() => setHover(false)}
      sx={{
        zIndex: selected === false ? -1 : undefined,
        width: '100%',
        pl: highlighted ? 1 : 0,
        pr: highlighted ? 1 : { xs: 0, sm: 1 },
        py: 0,
        border: theme => `2px solid ${bordercolor}`,
        backgroundColor: backgroundColor, // '#ffffff1f',
        // boxShadow: highlighted ? '0px 6px 6px -3px rgba(118, 176, 65,0.2),0px 10px 14px 1px rgba(118, 176, 65.14),0px 4px 18px 3px rgba(118, 176, 65,0.12);' : ''
        boxShadow: highlighted
          ? '0px 6px 6px -3px rgb(118, 176, 65,0.8), 0px 10px 14px 1px rgb(118, 176, 65, 0.5), 0px 4px 18px 3px rgb(118, 176, 65, 0.5)'
          : '',
      }}
      key={key}
    >
      <AlertDialog
        {...alertDetails}
        open={!!alertDetails?.open}
        title={alertDetails?.title || ''}
        content={alertDetails?.content || ''}
        form={alertDetails?.form}
        sx={alertDetails?.sx || {}}
        PaperProps={alertDetails?.PaperProps || {}}
        onClose={() => {
          alertDetails?.onClose && alertDetails.onClose();
          setAlertDetails(undefined);
        }}
        onConfirm={() => {
          alertDetails?.onConfirm && alertDetails.onConfirm();
          setAlertDetails(undefined);
        }}
      />
      <span ref={scrollRef}></span>
      <Grid container alignItems="center" rowSpacing={0}>
        <Grid
          item
          container
          rowSpacing={1}
          sx={{ width: { xs: '100%', sm: 'fit-content' }, mr: 'auto' }}
        >
          <Grid item>
            <Typography
              // onClick={onClick}
              variant={'subtitle1'}
              color={
                stageValues?.complete || color === 'complete'
                  ? 'disabled'
                  : 'primary'
              }
              sx={{
                height: 33,
                lineHeight: '33px',
                verticalAlign: 'middle',
                pl: 2,
                py: 1,
                fontWeight: 600,
                color: theme =>
                  stageValues?.complete || color === 'complete' || !active
                    ? theme.palette.text.disabled
                    : undefined,
              }}
            >
              {value}&nbsp;
            </Typography>
          </Grid>
        </Grid>

        {(true ||
          (showDetails && childrenPastDue !== 0) ||
          (showDetails && childrenAtRisk !== 0) ||
          (showDetails && numChildren !== 0) ||
          stageValues.status) && (
          <Grid item sx={{ width: { xs: '100%', sm: 'fit-content' } }}>
            <Grid container alignItems="center" spacing={1}>
              {showDetails &&
                !stageValues.complete &&
                statusCount &&
                Object.keys(statusCount).length > 0 && (
                  <Grid item xs={12} sm="auto">
                    <Tooltip
                      title={
                        <span>
                          {`${childrenPastDue} Deadline${
                            childrenPastDue > 1 ? 's' : ''
                          } Missed`}
                          <br />
                          {`${childrenAtRisk} Deadline${
                            childrenAtRisk > 1 ? 's' : ''
                          } Approaching`}
                          <br />
                          {`${statusCount?.['Complete'] || 0} Completed`}
                        </span>
                      }
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight:
                            childrenPastDue > 0 || childrenAtRisk > 0
                              ? 700
                              : undefined,
                          color: theme =>
                            childrenPastDue > 0
                              ? theme.palette.error.main
                              : childrenAtRisk > 0
                              ? theme.palette.warning.main
                              : 'inherit',
                        }}
                      >
                        <span style={{ color: 'black', fontWeight: 400 }}>{`${
                          statusCount?.['Complete'] || 0
                        }/`}</span>
                        {`${Object.values(statusCount)?.reduce(
                          (acc, b) => acc + b,
                          0
                        )}`}
                      </Typography>
                    </Tooltip>
                  </Grid>
                )}
              {((!disabled &&
                !template &&
                hover &&
                statuses &&
                statuses.length > 0) ||
                stageValues.status) && (
                <Grid item xs={12} sm="auto">
                  <StageButtonMenu
                    options={statuses || []}
                    label={stageValues.status || 'Add Outcome'}
                    onClick={async (outcome: any) => {
                      await onStatusChange(outcome, () => {
                        setStageValues((sv: any) => ({
                          ...sv,
                          status: outcome?.name,
                          complete: true,
                        }));
                      });
                    }}
                    onInfo={onStatusInfo}
                    onClear={
                      stageValues.status
                        ? () =>
                            setAlertDetails({
                              open: true,
                              PaperProps: {
                                sx: {
                                  border: (theme: Theme) =>
                                    `10px solid ${theme.palette.warning.light}`,
                                },
                              },
                              title: `Are you sure you want to remove the stage outcome?`,
                              content: ``,
                              onConfirm: async () => {
                                await onStatusChange(null, () => {
                                  setStageValues((sv: any) => ({
                                    ...sv,
                                    status: null,
                                    complete: false,
                                  }));
                                });
                              },
                              onClose: () => {},
                            })
                        : undefined
                    }
                    sx={{ width: '100%', boxShadow: theme => theme.shadows[1] }}
                  />
                </Grid>
              )}
              {color === 'default' && (
                <Grid
                  item
                  {...(downSm ? { xs: 12 } : {})}
                  sx={{ pl: 1 }}
                  onClick={event => event.stopPropagation()}
                >
                  <DateChip
                    range={false}
                    value={{
                      start: stageValues?.start_date
                        ? new Date(stageValues?.start_date)
                        : undefined,
                      end: stageValues?.due_date
                        ? new Date(stageValues?.due_date)
                        : undefined,
                    }}
                    onDateChange={({
                      start,
                      end,
                    }: {
                      start?: Date;
                      end?: Date;
                    }) => {
                      setStageValues((pv: any) => ({
                        ...pv,
                        start_date: start,
                        due_date: end,
                      }));
                      patch(onDateChange({ start, end }));
                      reloadParent();
                    }}
                    tooltip={'Add due date'}
                    disabled={disabled}
                  />
                </Grid>
              )}

              {color === 'pastdue' && (
                <Grid item {...(downSm ? { xs: 12 } : {})} sx={{ pl: 1 }}>
                  <DateChip
                    range={false}
                    value={{
                      start: stageValues?.start_date
                        ? new Date(stageValues?.start_date)
                        : undefined,
                      end: stageValues?.due_date
                        ? new Date(stageValues?.due_date)
                        : undefined,
                    }}
                    onDateChange={({
                      start,
                      end,
                    }: {
                      start?: Date;
                      end?: Date;
                    }) => {
                      setStageValues((pv: any) => ({
                        ...pv,
                        start_date: start,
                        due_date: end,
                      }));
                      patch(onDateChange({ start, end }));
                      reloadParent();
                    }}
                    tooltip={'Click to change date'}
                    disabled={disabled}
                  />
                </Grid>
              )}

              {(color === 'warning' || color === 'ontrack') && (
                <Grid item {...(downSm ? { xs: 12 } : {})} sx={{ pl: 1 }}>
                  <DateChip
                    range={false}
                    value={{
                      start: stageValues?.start_date
                        ? new Date(stageValues?.start_date)
                        : undefined,
                      end: stageValues?.due_date
                        ? new Date(stageValues?.due_date)
                        : undefined,
                    }}
                    onDateChange={({
                      start,
                      end,
                    }: {
                      start?: Date;
                      end?: Date;
                    }) => {
                      setStageValues((pv: any) => ({
                        ...pv,
                        start_date: start,
                        due_date: end,
                      }));
                      patch(onDateChange({ start, end }));
                      reloadParent();
                    }}
                    tooltip={'Click to change date'}
                    disabled={disabled}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        )}

        {/* <Grid item xs={12}>
            <Divider />
          </Grid> */}
      </Grid>
    </Paper>
  );
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  input: {
    padding: '5px 5px',
  },
}));
